import {
  MotifButton,
  MotifErrorMessage,
  MotifProgressLoader,
  MotifToast,
} from "@ey-xd/motif-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useGetCaseSummaryQuery } from "@/services/caseSummary/caseSummaryReducer";
import SummaryForm from "../SummaryForm/SummaryForm";
import "./SummaryCaseDetails.scss";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCase } from "@/features/case/use-case";
import { useGetCaseUsersListQuery } from "../SummaryForm/summaryHelper";
import { havingEditPermission } from "@/utility/common";
import { EDIT_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import CaseContext from "../../CaseContext";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { paginationPageSizeSelector } from "@/Constant";
import Table from "@/components/Table/table";
import { useSummaryHeaderDefinitions } from "../SummaryForm/ColHeaderDetails";
import { customTableLoadingData } from "@/components/Table/CustomPaginationConstants";
import { useDispatch, useSelector } from "react-redux";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";

export default function SummaryCaseDetails() {
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const caseContextData: any = useContext<any>(CaseContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { caseId } = useParams<{ caseId: string }>();
  const caseData = useCase();
  const permissions = useSelector((state: any) => state.permissions);
  const retainState = useSelector(
    (e: any) => e.retainState.caseDetailsViewTabState
  );
  const { Case: permissionsOfCase } = permissions;
  let casePermissions = permissionsOfCase || [];
  const { data, error, isLoading, refetch } = useGetCaseSummaryQuery(
    caseId || caseData?.caseId
  );
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      caseId: caseData.caseId || "",
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseData?.caseId, retainState]);
  const paginationObj = getPaginationObj;
  const columnDefs = useSummaryHeaderDefinitions();

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "caseDetailsViewTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }

  let {
    data: groupList,
    isFetching: fetchingListUsers,
    isError: caseUsersError,
    isLoading: caseUsersLoading,
    refetch: refetchCaseUsersList,
    status: caseUsersStatus,
  } = useGetCaseUsersListQuery(paginationObj);
  useEffect(() => {
    refetch();
    refetchCaseUsersList();
    //eslint-disable-next-line
  }, [notification]);

  const caseDetails = [
    {
      title: "pages.caseSummary.fieldLabelDetails.caseName",
      value: data?.caseName,
    },

    {
      title: "pages.caseSummary.fieldLabelDetails.caseDescription",
      value: data?.caseDescription,
    },
    {
      title: "pages.caseSummary.fieldLabelDetails.caseShortName",
      value: data?.caseShortName,
    },
    {
      title: "pages.caseSummary.fieldLabelDetails.status",
      value:
        data?.caseStatus === false
          ? t("pages.caseSummary.fieldLabelDetails.statusClose")
          : t("pages.caseSummary.fieldLabelDetails.statusOpen"),
    },
    {
      title: "pages.caseSummary.fieldLabelDetails.caseNameFrench",
      value: data?.caseNameFrench,
    },
    {
      title: "pages.caseSummary.fieldLabelDetails.caseDescriptionFrench",
      value: data?.caseDescriptionFrench,
    },
    {
      title: "pages.caseSummary.fieldLabelDetails.notificationRecipients",
      value: data?.notificationRecipients,
    },
  ];

  const closeMessageHandler = () => {
    setNotification({ ...notification, show: false });
  };

  const renderMessage = () => {
    if (notification.show) {
      window.scrollTo(0, 0);
      return (
        <MotifToast
          onClose={closeMessageHandler}
          //@ts-ignore
          variant={notification.type}
        >
          {" "}
          {notification.type === "error"
            ? t("global.globalNotifications.unExpectedError")
            : t("global.globalNotifications.updatedSuccessfully")}
        </MotifToast>
      );
    }
  };

  if (isLoading) {
    return <MotifProgressLoader show variant="default" />;
  }
  if (error) {
    return (
      <MotifErrorMessage>
        {t("global.globalNotifications.wentWrong")}
      </MotifErrorMessage>
    );
  }

  if (caseContextData.editCase) {
    //@ts-ignore
    return (
      <SummaryForm
        summaryData={data}
        notification={notification}
        setNotification={setNotification}
      />
    );
  }

  interface CaseDetail {
    title: string;
    value: string;
  }

  const numberOfColumns = 4;

  const chunkArray = (array: CaseDetail[], chunkSize: number) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };
  const caseDetailsFields = chunkArray(caseDetails, numberOfColumns);

  return (
    <>
      {renderMessage()}
      <div className="motif-container case-summary-form pad-v-20">
        <div className="motif-row align-items-center">
          <div className="motif-col-lg-11">
            <div className="motif-body1-default-regular page-title">
              {t("pages.caseSummary.title")}
            </div>
            <div className="motif-body2-default-regular page-description">
              {t("pages.caseSummary.description")}
            </div>
          </div>
          <div className="motif-col-lg-1">
            {havingEditPermission(casePermissions) && (
              <MotifButton
                onClick={() => caseContextData.setEditCase(true)}
                size="small"
                type="submit"
                variant={EDIT_BUTTON_VARIANT}
              >
                {t("global.globalActions.edit")}
              </MotifButton>
            )}
          </div>
        </div>
      </div>
      <div className="motif-container main-container pad-v-40">
        {caseDetailsFields.map((caseDetailsField, rowIndex) => (
          <div
            key={rowIndex}
            className={`motif-row ${rowIndex > 0 ? "pad-t-30" : ""}`}
          >
            {caseDetailsField.map((detail, colIndex) => (
              <div key={colIndex} className="motif-col-lg-3">
                <div>
                  <div className="title">{t(detail.title)}</div>
                  <div className="motif-body2-default-bold overFlowWrapBreakWord">
                    {detail.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
        <div className="motif-row form-title">
          <div className="motif-col-xs-4">
            <div className="form-field">
              <div className="motif-body2-default-bold title">
                {t("pages.caseSummary.fieldLabelDetails.caseTeam")}
              </div>
            </div>
          </div>
        </div>
        {caseUsersLoading ? (
          <MotifProgressLoader></MotifProgressLoader>
        ) : (
          <>
            <Table
              columnDefs={columnDefs}
              cellRenderers={fetchingListUsers ? [] : groupList?.results}
              columnTypes={{ ...retainState.sortedColumnTypes }}
              handlePaginationData={handlePaginationData}
              paginationObj={paginationObj}
              TitleComplement={() => <></>}
              overlayNoRowsTemplate={
                fetchingListUsers
                  ? t(customTableLoadingData)
                  : caseUsersError
                  ? t("global.globalNotifications.wentWrong")
                  : t("global.globalNotifications.noRecords")
              }
              title={""}
              description={""}
              pagination={false}
            />
            <CustomPagination
              apiData={groupList}
              apiStatus={caseUsersStatus}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!groupList?.hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "caseDetailsViewTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "caseDetailsViewTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "caseDetailsViewTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          </>
        )}
      </div>
    </>
  );
}
